// Emergency Care is intentionally first; the rest are alphabetical.
export const VET_SERVICES = [
  "Emergency Care",
  "Behavioural Consultation",
  "Blood Test",
  "Dental Check-up",
  "End of Life Care",
  "Equine Dental Care",
  "Equine Lameness Exam",
  "Farm Call / Mobile Visit",
  "Farrier Referral",
  "Flea & Worm Treatment",
  "Fertility & Breeding Services",
  "General Consultation",
  "Grooming",
  "Health Check",
  "Herd Health Plan",
  "Hoof Care",
  "Horse Vaccination",
  "Livestock Vaccination",
  "Microchipping",
  "Nail Trimming",
  "Neutering / Spaying",
  "Pre-Purchase Equine Exam",
  "Puppy / Kitten Check",
  "Surgery",
  "TB Testing",
  "Ultrasound",
  "Vaccination",
  "Worming (Equine & Livestock)",
  "X-Ray",
] as const;

export type VetServiceName = (typeof VET_SERVICES)[number];

export const EMERGENCY_SERVICE: VetServiceName = "Emergency Care";

export type VetServiceCategory = "Emergency" | "GetVideoVet" | "Small Animal" | "Equestrian" | "Livestock";

const EQUESTRIAN_SERVICES: VetServiceName[] = [
  "Equine Dental Care",
  "Equine Lameness Exam",
  "Farrier Referral",
  "Hoof Care",
  "Horse Vaccination",
  "Pre-Purchase Equine Exam",
];

const LIVESTOCK_SERVICES: VetServiceName[] = [
  "Herd Health Plan",
  "Livestock Vaccination",
  "TB Testing",
];

// Services that apply to both equestrian & livestock (mixed/large animal)
const MIXED_LARGE_ANIMAL_SERVICES: VetServiceName[] = [
  "Farm Call / Mobile Visit",
  "Worming (Equine & Livestock)",
];

export const GETVIDEOVET_SERVICES = ["Video Call", "Audio Call"] as const;
export type GetVideoVetService = (typeof GETVIDEOVET_SERVICES)[number];

export const GETVIDEOVET_SERVICE_DESCRIPTIONS: Record<GetVideoVetService, string> = {
  "Video Call": "Live video consultation with a vet from the comfort of home.",
  "Audio Call": "Voice-only consultation when video isn't needed or available.",
};

export const VET_SERVICE_CATEGORIES: { label: VetServiceCategory; services: readonly string[] }[] = [
  {
    label: "Emergency",
    services: [EMERGENCY_SERVICE],
  },
  {
    label: "GetVideoVet",
    services: GETVIDEOVET_SERVICES,
  },
  {
    label: "Small Animal",
    services: VET_SERVICES.filter(
      (s) =>
        s !== EMERGENCY_SERVICE &&
        !EQUESTRIAN_SERVICES.includes(s) &&
        !LIVESTOCK_SERVICES.includes(s) &&
        !MIXED_LARGE_ANIMAL_SERVICES.includes(s),
    ),
  },
  {
    label: "Equestrian",
    services: [...EQUESTRIAN_SERVICES, ...MIXED_LARGE_ANIMAL_SERVICES].sort(),
  },
  {
    label: "Livestock",
    services: [...LIVESTOCK_SERVICES, ...MIXED_LARGE_ANIMAL_SERVICES].sort(),
  },
];

// Default descriptions surfaced when a vet selects a service in the dashboard.
// Kept short, owner-friendly, and generic so practices can edit to suit.
export const VET_SERVICE_DESCRIPTIONS: Record<VetServiceName, string> = {
  "Emergency Care": "Urgent, same-day treatment for sudden illness, injury, or life-threatening conditions.",
  "Behavioural Consultation": "Assessment and guidance for behavioural concerns such as anxiety, aggression, or training issues.",
  "Blood Test": "Diagnostic blood sampling and laboratory analysis to assess overall health or investigate symptoms.",
  "Dental Check-up": "Oral examination to check for tartar, gum disease, and dental pain, with treatment recommendations.",
  "End of Life Care": "Compassionate palliative support and humane euthanasia, with guidance for owners through the process.",
  "Equine Dental Care": "Routine rasping, oral exams, and treatment of dental issues for horses and ponies.",
  "Equine Lameness Exam": "Thorough lameness workup including gait assessment, flexion tests, and diagnostic imaging where needed.",
  "Farm Call / Mobile Visit": "On-site veterinary visits to your farm, yard, or stable for examinations and treatments.",
  "Farrier Referral": "Coordination with trusted farriers for corrective shoeing and hoof rehabilitation plans.",
  "Flea & Worm Treatment": "Preventative parasite control tailored to your pet's lifestyle, age, and weight.",
  "Fertility & Breeding Services": "Breeding soundness exams, ovulation timing, artificial insemination, and pregnancy diagnosis.",
  "General Consultation": "Standard veterinary consultation to discuss concerns, examine your pet, and plan treatment.",
  "Grooming": "Professional bathing, brushing, and coat care to keep your pet comfortable and skin healthy.",
  "Health Check": "Comprehensive nose-to-tail examination to monitor wellbeing and catch issues early.",
  "Herd Health Plan": "Tailored programme covering vaccination, parasite control, fertility, and biosecurity for your herd.",
  "Hoof Care": "Routine hoof trimming, balancing, and treatment of common hoof problems.",
  "Horse Vaccination": "Core and risk-based vaccinations for equines, including flu, tetanus, and herpes virus.",
  "Livestock Vaccination": "Scheduled vaccinations for cattle, sheep, pigs, and goats to protect herd and flock health.",
  "Microchipping": "Quick, permanent ID microchip implant with registration to a national pet database.",
  "Nail Trimming": "Safe trimming of nails or claws to prevent overgrowth, splitting, and discomfort.",
  "Neutering / Spaying": "Surgical neutering or spaying with full pre-op assessment and post-op aftercare advice.",
  "Pre-Purchase Equine Exam": "Independent vetting examination before purchase, with a written report on the horse's suitability.",
  "Puppy / Kitten Check": "First health check, vaccination start, parasite control, and new-owner advice for puppies and kittens.",
  "Surgery": "Routine and advanced surgical procedures performed in a sterile theatre with full anaesthetic monitoring.",
  "TB Testing": "Statutory bovine tuberculosis testing carried out by approved veterinary surgeons.",
  "Ultrasound": "Non-invasive ultrasound imaging for soft tissue assessment, pregnancy diagnosis, and more.",
  "Vaccination": "Core and lifestyle vaccinations to protect against common infectious diseases.",
  "Worming (Equine & Livestock)": "Targeted worming programmes based on faecal egg counts and seasonal risk.",
  "X-Ray": "Digital radiography for diagnosing bone, joint, chest, and abdominal conditions.",
};
